/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { Typography2 } from '@coursera/cds-core';
import { StarFilledIcon, StarHalfFilledIcon, StarIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/reviews-common';

const MAX_STARS = 5;

type Props = {
  rating: number;
  numReviews?: number;
  size?: React.ComponentProps<typeof StarFilledIcon>['size'];
  color?: React.ComponentProps<typeof StarFilledIcon>['color'];
};

const styles = {
  container: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--cds-spacing-50);
    color: var(--cds-color-neutral-primary);
    line-height: 1;
  `,
  stars: css`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    line-height: 0;
    vertical-align: middle;
  `,
  starWrapper: css`
    display: inline-flex;
    align-items: center;
    line-height: 0;
  `,
  ratingNumber: css`
    font-weight: 700;
    color: var(--cds-color-neutral-primary);
    margin-left: var(--cds-spacing-50);
  `,
  dotSeparator: css`
    margin: 0 var(--cds-spacing-100);
    color: var(--cds-color-neutral-primary-weak);
  `,
  reviewCount: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
};

const RatingLine = ({ rating, numReviews, size = 'small', color = 'default' }: Props) => {
  const roundedForStars = Math.round(rating * 2) / 2;
  const fullCount = Math.floor(roundedForStars);
  const hasHalf = roundedForStars % 1 === 0.5;
  const displayRating = rating.toFixed(1);

  const stars: React.ReactNode[] = [];
  for (let i = 0; i < fullCount; i++) {
    stars.push(
      <span key={`full-${i}`} css={styles.starWrapper} data-testid="rating-line-star-full">
        <StarFilledIcon color={color} size={size} aria-hidden />
      </span>
    );
  }
  if (hasHalf) {
    stars.push(
      <span key="half" css={styles.starWrapper} data-testid="rating-line-star-half">
        <StarHalfFilledIcon color={color} size={size} aria-hidden />
      </span>
    );
  }
  for (let i = stars.length; i < MAX_STARS; i++) {
    stars.push(
      <span key={`empty-${i}`} css={styles.starWrapper} data-testid="rating-line-star-empty">
        <StarIcon color={color} size={size} aria-hidden />
      </span>
    );
  }

  return (
    <div
      css={styles.container}
      data-testid="rating-line"
      role="img"
      aria-roledescription="rating"
      aria-label={_t('Rated #{displayRating} out of 5 stars', { displayRating })}
    >
      <span css={styles.stars} aria-hidden>
        {stars}
      </span>
      <Typography2 component="span" variant="subtitleSmall" css={styles.ratingNumber}>
        {displayRating}
      </Typography2>
      {numReviews && (
        <React.Fragment>
          <span css={styles.dotSeparator} aria-hidden>
            ·
          </span>
          <Typography2 component="span" variant="bodySecondary" css={styles.reviewCount}>
            <FormattedMessage message={_t('{numReviews} reviews')} numReviews={numReviews.toLocaleString()} />
          </Typography2>
        </React.Fragment>
      )}
    </div>
  );
};

export default RatingLine;
